import { Account, SegmentReportLine } from '../interfaces/accounting.interface';

/**
 * Account tree behind the expandable statements (the client twin of the backend's ReportTree).
 * Every ancestor of a reported account becomes a group row whose figures are its own postings plus
 * its descendants'; a group that also carries postings of its own shows them as an extra
 * "(direct)" child so a subtotal always equals the rows beneath it.
 */
export interface TreeLine { accountId: string; code: string; name: string; values: number[] }

export interface TreeRow {
    key: string;
    accountId: string;
    code: string;
    name: string;
    depth: number;
    group: boolean;
    direct: boolean;
    totals: number[];
}

interface Node {
    id: string; code: string; name: string; own: number[] | null; children: Node[];
}

const addVec = (a: number[], b: number[]) => a.map((v, i) => v + (b[i] ?? 0));

/** Lines of one section across periods (index 0 = current), dropping accounts that are zero in every period. */
export function mergePeriodLines(perPeriod: SegmentReportLine[][]): TreeLine[] {
    const n = perPeriod.length;
    const byId = new Map<string, TreeLine>();
    perPeriod.forEach((lines, p) => {
        (lines ?? []).forEach(l => {
            let t = byId.get(l.accountId);
            if (!t) {
                t = { accountId: l.accountId, code: l.accountCode, name: l.accountName, values: new Array(n).fill(0) };
                byId.set(l.accountId, t);
            }
            t.values[p] += l.netBalance ?? 0;
        });
    });
    return Array.from(byId.values()).filter(t => t.values.some(v => Math.abs(v) >= 0.005));
}

export function buildReportTree(lines: TreeLine[], accounts: Account[], columns: number): TreeRow[] {
    const refs = new Map<string, Account>();
    accounts.forEach(a => { if (a.id) refs.set(a.id, a); });
    const nodes = new Map<string, Node>();
    const parentOf = new Map<string, string>();

    for (const line of lines) {
        let n = nodes.get(line.accountId);
        if (!n) {
            n = { id: line.accountId, code: line.code, name: line.name, own: null, children: [] };
            nodes.set(line.accountId, n);
        }
        const vals = Array.from({ length: columns }, (_, i) => line.values[i] ?? 0);
        n.own = n.own ? addVec(n.own, vals) : vals;

        const seen = new Set<string>([line.accountId]);
        let child = line.accountId;
        let current = refs.get(line.accountId)?.parentAccountId;
        while (current && seen.has(current) === false) {
            seen.add(current);
            const pr = refs.get(current);
            if (!pr) break;
            if (!nodes.has(current)) nodes.set(current, { id: current, code: pr.code, name: pr.name, own: null, children: [] });
            parentOf.set(child, current);
            child = current;
            current = pr.parentAccountId;
        }
    }

    const byCode = (a: Node, b: Node) => (a.code ?? '').localeCompare(b.code ?? '');
    const roots: Node[] = [];
    nodes.forEach(n => {
        const p = parentOf.get(n.id);
        const parent = p ? nodes.get(p) : undefined;
        if (parent && parent !== n) parent.children.push(n); else roots.push(n);
    });
    roots.sort(byCode);

    const out: TreeRow[] = [];
    const walk = (n: Node, depth: number, path: Set<string>): number[] => {
        let total = n.own ? [...n.own] : new Array(columns).fill(0);
        if (n.children.length === 0 || path.has(n.id)) {
            out.push({ key: n.id, accountId: n.id, code: n.code, name: n.name, depth, group: false, direct: false, totals: total });
            return total;
        }
        path.add(n.id);
        n.children.sort(byCode);
        const slot = out.length;
        out.push(null as unknown as TreeRow);
        if (n.own) {
            out.push({ key: `${n.id}#direct`, accountId: n.id, code: n.code, name: `${n.name} (direct)`, depth: depth + 1, group: false, direct: true, totals: [...n.own] });
        }
        n.children.forEach(c => { total = addVec(total, walk(c, depth + 1, path)); });
        path.delete(n.id);
        out[slot] = { key: n.id, accountId: n.id, code: n.code, name: n.name, depth, group: true, direct: false, totals: total };
        return total;
    };
    roots.forEach(r => walk(r, 0, new Set()));
    return out;
}

export type ExpandMode = 'default' | 'all' | 'none';

/**
 * Rows to show given the expand state. Default: roots expanded, so the first two levels are
 * visible; 'all' expands every group; 'none' collapses everything to the roots. A per-group
 * override (toggle) wins over the mode.
 */
export function visibleRows(rows: TreeRow[], mode: ExpandMode, overrides: Record<string, boolean>, scope: string): TreeRow[] {
    const isExpanded = (r: TreeRow) => {
        const k = `${scope}:${r.key}`;
        if (k in overrides) return overrides[k];
        if (mode === 'all') return true;
        if (mode === 'none') return false;
        return r.depth === 0;
    };
    const out: TreeRow[] = [];
    let hideBelow: number | null = null;
    for (const r of rows) {
        if (hideBelow !== null) {
            if (r.depth > hideBelow) continue;
            hideBelow = null;
        }
        out.push(r);
        if (r.group && !isExpanded(r)) hideBelow = r.depth;
    }
    return out;
}

export const isGroupExpanded = (r: TreeRow, mode: ExpandMode, overrides: Record<string, boolean>, scope: string): boolean => {
    const k = `${scope}:${r.key}`;
    if (k in overrides) return overrides[k];
    if (mode === 'all') return true;
    if (mode === 'none') return false;
    return r.depth === 0;
};
